/**
 * Google Analytics Utilities
 *
 * Provides helper functions for tracking page views and custom events
 */

import ReactGA from "react-ga4";
import { analyticsConfig } from "@/config/analytics";

let isInitialized = false;

/**
 * Initialize Google Analytics
 * Call this once at app startup
 */
export const initializeAnalytics = (): void => {
  if (isInitialized) {
    if (analyticsConfig.debug) {
      console.warn("[Analytics] Already initialized");
    }
    return;
  }

  if (!analyticsConfig.enabled) {
    if (analyticsConfig.debug) {
      console.log(
        "[Analytics] Disabled - no measurement ID or not in production",
      );
    }
    return;
  }

  try {
    if (analyticsConfig.debug) {
      console.log(
        "[Analytics] Initializing with measurement ID:",
        analyticsConfig.measurementId,
      );
    }

    // ReactGA.initialize handles script loading, gtag setup, and configuration
    ReactGA.initialize(analyticsConfig.measurementId, {
      gtagOptions: {
        send_page_view: false, // We handle page views manually
        debug_mode: analyticsConfig.debug,
      },
    });

    isInitialized = true;

    if (analyticsConfig.debug) {
      console.log("[Analytics] Initialization complete");
      console.log(
        "[Analytics] window.gtag available:",
        typeof window.gtag !== "undefined",
      );
      console.log(
        "[Analytics] window.dataLayer available:",
        typeof window.dataLayer !== "undefined",
      );
    }
  } catch (error) {
    console.error("[Analytics] Failed to initialize:", error);
  }
};

/**
 * Track a page view
 * @param path - The page path (e.g., '/home', '/sessions/123')
 * @param title - Optional page title
 */
export const trackPageView = (path: string, title?: string): void => {
  if (analyticsConfig.debug) {
    console.log("[Analytics] trackPageView called:", {
      path,
      title,
      enabled: analyticsConfig.enabled,
      isInitialized,
      measurementId: analyticsConfig.measurementId,
    });
  }

  if (!analyticsConfig.enabled || !isInitialized) {
    if (analyticsConfig.debug) {
      console.log(
        "[Analytics] Page view NOT sent - disabled or not initialized",
      );
    }
    return;
  }

  try {
    if (analyticsConfig.debug) {
      console.log("[Analytics] Sending page view...");
    }

    // Send page view using ReactGA
    // GA4 uses event-based tracking, so we use the event method
    ReactGA.send({
      hitType: "pageview",
      page: path,
      title: title || document.title,
    });

    if (analyticsConfig.debug) {
      console.log("[Analytics] Page view sent:", { path, title });
      console.log(
        "[Analytics] Check Network tab for /g/collect or /collect requests",
      );
    }
  } catch (error) {
    console.error("[Analytics] Failed to track page view:", error);
  }
};

/**
 * Track a custom event
 * @param category - Event category (e.g., 'User', 'Session', 'Video')
 * @param action - Event action (e.g., 'login', 'upload', 'play')
 * @param label - Optional event label
 * @param value - Optional numeric value
 */
const trackEvent = (
  category: string,
  action: string,
  label?: string,
  value?: number,
): void => {
  if (!analyticsConfig.enabled || !isInitialized) {
    if (analyticsConfig.debug) {
      console.log("[Analytics] Event tracked (debug):", {
        category,
        action,
        label,
        value,
      });
    }
    return;
  }

  try {
    // Send via ReactGA (which uses gtag under the hood)
    ReactGA.event({
      category,
      action,
      label,
      value,
    });

    if (analyticsConfig.debug) {
      console.log("[Analytics] Event sent:", {
        category,
        action,
        label,
        value,
      });
    }
  } catch (error) {
    console.error("[Analytics] Failed to track event:", error);
  }
};

/**
 * Track specific user actions for this application
 */
export const analytics = {
  // User actions
  user: {
    login: () => trackEvent("User", "login"),
    logout: () => trackEvent("User", "logout"),
    register: () => trackEvent("User", "register"),
  },

  // Session actions
  session: {
    create: () => trackEvent("Session", "create"),
    view: (sessionId: string) => trackEvent("Session", "view", sessionId),
    delete: () => trackEvent("Session", "delete"),
    upload: (fileType: string) => trackEvent("Session", "upload", fileType),
  },

  // Analytics page actions
  sessionAnalytics: {
    view: (sessionId: string) =>
      trackEvent("SessionAnalytics", "view", sessionId),
    toggleChart: (chartName: string, visible: boolean) =>
      trackEvent(
        "SessionAnalytics",
        "toggle_chart",
        chartName,
        visible ? 1 : 0,
      ),
    exportData: (format: string) =>
      trackEvent("SessionAnalytics", "export", format),
  },

  // Navigation
  navigation: {
    pageView: (path: string, title?: string) => trackPageView(path, title),
  },

  // Video actions
  video: {
    play: (sessionId: string) => trackEvent("Video", "play", sessionId),
    pause: (sessionId: string) => trackEvent("Video", "pause", sessionId),
    upload: () => trackEvent("Video", "upload"),
  },

  // Performance tracking
  performance: {
    apiCall: (endpoint: string, duration: number) =>
      trackEvent("Performance", "api_call", endpoint, Math.round(duration)),
    pageLoad: (path: string, duration: number) =>
      trackEvent("Performance", "page_load", path, Math.round(duration)),
  },
};
